import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== コミュニケーション技術研修 詳細 全面作り直し（Implementer C スコープ） =====
// デザイン土台: home / about/overview の新スタイル。実データ変更禁止・現行working tree実文のみ・創作禁止。

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

const OVERVIEW = [
  '執事とゲストのコミュニケーションは、単なる情報伝達ではありません。ゲストの言葉の背景にある本当のニーズを察知し、状況に応じた最適な対応を提供する。そのためには、相手の話を深く聴き、適切な質問で真意を引き出し、誤解のない明確な言葉で応答する技術が必要です。また、言葉だけでなく、表情、姿勢、声のトーンといった非言語要素も重要なコミュニケーション手段となります。',
  '本研修では、執事のコミュニケーション技術を理論と実践の両面から学びます。アクティブリスニング（積極的傾聴）の技術、効果的な質問のフレームワーク、明確で誤解のない表現方法、非言語コミュニケーション（ボディランゲージ、表情、声のトーン）の活用、状況に応じたコミュニケーションスタイルの使い分け、難しい会話の進め方まで、実務で即活用できる内容を習得。ペアワークとグループディスカッションを通じて、実践的なコミュニケーション力を体得します。',
  '営業職、接客業、管理職、チームリーダー、すべてのビジネスパーソンに共通する「伝える力・聴く力」を高めたい方に最適な研修です。',
];

const FEATURES = [
  { icon: 'ri-ear-line', t: '傾聴重視', d: '聴く力が伝える力の土台になります。' },
  { icon: 'ri-emotion-line', t: '非言語活用', d: '言葉以外の表現技術を学びます。' },
  { icon: 'ri-flashlight-line', t: '即実践可能', d: '明日から使えるスキルが身につきます。' },
];

const CURRICULUM = [
  { part: '第1部', time: '45分', title: '傾聴技術の習得', desc: 'アクティブリスニングの原則、相手の話を引き出す技術、共感的理解の実践、傾聴を妨げる要因と対策を学びます。執事が実践する「相手の立場に立った」傾聴技術、言葉の背景にある真意を読み取る方法、効果的な相槌と反応の技術を習得します。理論だけでなく、実際の執事の現場での事例を通じて、深い傾聴力を身につけます。' },
  { part: '第2部', time: '45分', title: '効果的な質問力と表現力', desc: 'オープン質問とクローズド質問の使い分け、明確で誤解のない表現方法、肯定的な言葉の選択、フィードバックの技術を習得します。執事が実践する「適切なタイミング」での質問技術、相手の心を開く質問の仕方、誤解を生まない明確な表現方法を学びます。実際のビジネスシーンを想定した演習を通じて、実践的な質問力と表現力を身につけます。' },
  { part: '第3部', time: '45分', title: '非言語コミュニケーション', desc: 'ボディランゲージの理解と活用、表情のコントロール、声のトーンと話し方、パーソナルスペースの使い方を学びます。執事が実践する「言葉以上に語る」非言語表現の技術、相手に安心感を与える姿勢と表情、信頼関係を築く声のトーンの使い方を習得します。実際の対話場面を想定した演習で、非言語コミュニケーションの効果的な活用方法を身につけます。' },
  { part: '第4部', time: '45分', title: '実践演習とケーススタディ', desc: 'ペアワークによる実践、難しい会話への対応、実際のケースを用いた演習で実践力を養います。執事が実践する「冷静な対応」と「建設的な対話」の技術、感情的な場面での適切なコミュニケーション、クレーム対応や意見の対立を建設的な議論に導く方法を学びます。実際のケーススタディを通じて、現場で使えるコミュニケーションスキルを身につけます。' },
];

const RECOMMENDS = [
  '営業職・販売職',
  '接客業・サービス業全般',
  '管理職・チームリーダー',
  'カスタマーサポート部門',
  '人事・総務部門',
  'すべてのビジネスパーソン',
  'コミュニケーションに課題を感じる部門',
  '新入社員研修としても最適',
];

const OUTCOMES = [
  { t: '傾聴力の向上', d: '相手の話を深く理解し、真意を引き出す力を身につけます。アクティブリスニングの技術により、より良い人間関係を築けるようになります。' },
  { t: '表現力の強化', d: '自分の意図を明確に、誤解なく伝える技術を習得します。効果的な質問と表現により、コミュニケーションの質が向上します。' },
  { t: '非言語コミュニケーション力', d: '言葉以外の要素を効果的に使い、コミュニケーションの質を高めます。表情、姿勢、声のトーンを意識的に活用できるようになります。' },
  { t: '対人関係の改善', d: '円滑なコミュニケーションにより、職場の人間関係が改善します。信頼関係を築く対話の技術を身につけます。' },
];

const SPECS = [
  { k: '研修時間', v: '1日3時間（午前/午後選択可）' },
  { k: '対象人数', v: '10-20名' },
  { k: '対象レベル', v: '全レベル（新入社員〜ベテラン）' },
  { k: '実施場所', v: '貴社会議室または当協会会場' },
  { k: 'カスタマイズ', v: '内容調整可能（別途相談）' },
];

const INCLUDES = ['研修プログラム実施', 'テキスト教材（参加者人数分）', 'コミュニケーション実践ガイド', '修了証（参加者全員）', '講師交通費（都内の場合）', '事後フォローアップ（1回）'];

const FLOW = [
  { t: 'お問い合わせ・ご相談', d: 'まずはお気軽にお問い合わせください。貴社の課題や目標をヒアリングいたします。' },
  { t: 'ヒアリング・カスタマイズ', d: '参加者の属性、業務内容、研修の目的などを詳しく伺い、最適なプログラムを設計します。' },
  { t: '日程調整・ご契約', d: '実施日時、場所、料金を確定し、ご契約となります。' },
  { t: '研修実施', d: '経験豊富な講師による実践的な研修を実施します。' },
  { t: 'フォローアップ', d: '研修後も継続的にフォローアップし、現場での実践をサポートします。' },
];

const FAQS = [
  { q: 'オンラインでの実施は可能ですか？', a: 'はい、オンライン形式での実施も対応可能です。別途お見積もりいたします。' },
  { q: '新入社員でも受講できますか？', a: 'はい、全レベル対応ですので新入社員からベテラン社員まで幅広く受講いただけます。' },
  { q: '営業職以外でも役立ちますか？', a: 'はい、すべての職種で必要なコミュニケーションの基本を学ぶ内容です。' },
  { q: '人前で話すプレゼンテーション技術も学べますか？', a: '本研修は対話的コミュニケーションが中心ですが、表現力の基礎は共通です。' },
  { q: '実施場所はどこになりますか？', a: '貴社会議室での実施が基本です。当協会会場での実施も可能です。' },
  { q: '事前準備は必要ですか？', a: 'コミュニケーションで困った経験や改善したい点をメモしてご参加ください。' },
  { q: 'フォローアップの内容は？', a: '研修後1ヶ月以内に、実践状況を確認し、追加アドバイスを行います。' },
  { q: '修了証は発行されますか？', a: 'はい、参加者全員に修了証を発行いたします。' },
  { q: '他の研修と組み合わせることは可能ですか？', a: 'はい。複数研修の組み合わせも可能です。割引もございます。' },
  { q: '講師はどのような方ですか？', a: 'Master Butler資格保持者で、実際の執事経験と研修実績が豊富な講師が担当します。' },
  { q: 'ペアワークの内容は？', a: '傾聴練習や質問の練習など、実際に対話しながら学ぶ演習を行います。' },
  { q: '非言語コミュニケーションとは具体的に何ですか？', a: '表情、姿勢、声のトーン、視線など、言葉以外で伝わる情報のことです。' },
  { q: '難しい会話とはどんな場面ですか？', a: 'クレーム対応、断りの伝達、意見の対立など、伝えにくい内容の会話です。' },
];

const RELATED = [
  { label: 'チームリーダーシップ研修', href: '/seminars/corporate/team-leadership', desc: '執事に学ぶリーダーシップの本質とマネジメント技術' },
  { label: 'ホスピタリティ向上研修', href: '/seminars/corporate/hospitality-enhancement', desc: '執事に学ぶ心からのおもてなしの技術' },
  { label: '企業向け研修プログラム一覧', href: '/seminars', desc: 'すべての研修プログラムを見る' },
];

export default function CommunicationSkillsPage() {
  useEffect(() => {
    updateSEO({
      title: 'コミュニケーション技術研修 | 執事に学ぶ伝える力 | 全日本執事協会',
      description: '全日本執事協会の企業向けコミュニケーション技術研修。執事が実践する傾聴技術、表現力向上、非言語コミュニケーションを学ぶプログラム。全レベル対応。1日3時間、10-20名対応。料金400,000円。',
      keywords: 'コミュニケーション技術研修,企業研修,傾聴技術,表現力,非言語コミュニケーション,執事,全日本執事協会',
      canonical: 'https://butler-association.jp/seminars/corporate/communication-skills',
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'セミナー・イベント', href: '/seminars' },
    { label: 'コミュニケーション技術研修' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-corp-communication-skills.webp" alt="コミュニケーション技術研修で傾聴と対話の技術を指導する執事と受講者" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Communication Skills</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">コミュニケーション技術研修</h1>
            <div className="space-y-5 font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              <p>
                執事が長年実践してきた高度なコミュニケーションスキルを、現代のビジネスパーソンに応用する実践的プログラムです。執事は言葉だけでなく、表情、姿勢、声のトーン、タイミングなど、あらゆる要素を駆使してゲストとコミュニケーションを取ります。
              </p>
              <p>
                相手の真意を正確に理解し、適切に応答する「傾聴力」、自分の意図を誤解なく伝える「表現力」、言葉以上に多くを語る「非言語コミュニケーション」の技術は、すべてのビジネスシーンで不可欠です。本研修では、傾聴の技術、効果的な質問力、表現力の向上、非言語コミュニケーションの活用まで、実務に直結するコミュニケーションスキルを体系的に習得します。
              </p>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
          <p className="border-l-2 border-[#a38e65] bg-[#f8f6f0] px-5 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
            ⚠️ 本研修はスキル習得を目的としており、業績向上や人間関係の改善を保証するものではありません。
          </p>
        </div>
      </div>

      <NavyBand no="I" ja="この研修について" en="Overview" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start mb-12 sm:mb-14">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-6">
                言葉の奥を聴き、<span className="text-[#a38e65]">誤解なく伝える</span>
              </h3>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                {OVERVIEW.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <img src="/images/generated/jba-p-031-comm-pair.webp" alt="対話を通じてコミュニケーション技術を磨く受講者の様子" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
          </div>
          <div className="grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] sm:border-b-0 sm:border-r last:border-r-0 py-7 sm:px-6 first:sm:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 新バリエーション①（番号付き時間バッジ・カリキュラム。由来: home IV 全幅エディトリアル行型の翻案） */}
      <NavyBand no="II" ja="研修カリキュラム（1日3時間）" en="Curriculum" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              コミュニケーション技術研修では、執事の現場経験に基づく4つの分野を体系的に学びます。各部は実践的な内容で構成され、知識だけでなく実際のコミュニケーション技術も習得できます。
            </p>
          </div>
          <img src="/images/generated/seq-communication-training-021890.webp" alt="講師の説明を聞きながら対話と傾聴を学ぶコミュニケーション研修" className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10" />
          <div className="border-t-2 border-[#1a2942]">
            {CURRICULUM.map((c) => (
              <div key={c.part} className="flex flex-col md:flex-row gap-4 md:gap-10 border-b border-[#e2ddd2] py-7">
                <div className="md:w-[220px] md:shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl leading-none">{c.part}</span>
                    <span className="inline-block border border-[#a38e65]/60 text-[#8f7a56] text-xs tracking-[0.06em] px-2.5 py-1 font-['Noto_Serif_JP',serif]">{c.time}</span>
                  </div>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mt-3">{c.title}</h4>
                </div>
                <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="III" ja="こんな企業・部門におすすめ" en="Ideal For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 border-t border-[#e2ddd2]">
            {RECOMMENDS.map((r) => (
              <div key={r} className="flex items-center gap-4 border-b border-[#e2ddd2] py-5 sm:px-5 first:sm:pl-0">
                <i className="ri-check-line text-xl text-[#a38e65] shrink-0"></i>
                <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942]">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="IV" ja="研修で得られる成果" en="Outcomes" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <img src="/images/generated/jba-p-032-comm-practice.webp" alt="研修を経て円滑な対話を実践する受講者の様子" className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10" />
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {OUTCOMES.map((o, i) => (
              <div key={o.t} className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8 flex flex-col">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{String(i + 1).padStart(2, '0')}</span>
                  <h4 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.5]">{o.t}</h4>
                </div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.95]">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 新バリエーション②（大数値+細罫スペック表+含有グリッド。由来: home IV Fee強調 + home VII 細罫テーブル） */}
      <NavyBand no="V" ja="実施形式と料金" en="Details & Fee" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-14 items-start">
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 text-center">
              <div className="font-['Noto_Serif_JP',serif] text-sm text-[#d7b779] tracking-[0.1em] mb-3">受講料（税込）</div>
              <div className="font-['Playfair_Display',serif] text-[#f8f6f0] text-5xl sm:text-6xl leading-none">400,000<span className="text-2xl ml-1">円</span></div>
              <div className="w-11 h-px bg-[#a38e65] mx-auto mt-6 mb-5"></div>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">1日3時間・10-20名でのご提供です。</p>
            </div>
            <div>
              <dl className="border-t-2 border-[#1a2942]">
                {SPECS.map((s) => (
                  <div key={s.k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[160px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{s.k}</dt>
                    <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{s.v}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="jba-display font-medium text-[#1a2942] text-lg mt-10 mb-5">料金に含まれるもの</h4>
              <ul className="grid sm:grid-cols-2 gap-x-8">
                {INCLUDES.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><i className="ri-check-line text-[#a38e65]"></i><span>{inc}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NavyBand no="VI" ja="お申込みの流れ" en="How to Apply" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <ol className="relative border-l border-[#e2ddd2] ml-5 max-w-4xl">
            {FLOW.map((s, i) => (
              <li key={s.t} className="relative pl-8 sm:pl-10 pb-9 last:pb-0">
                <span className="absolute -left-[19px] top-0 w-9 h-9 rounded-full bg-[#1a2942] text-[#d7b779] flex items-center justify-center font-['Playfair_Display',serif] text-sm border border-[#a38e65]/50">{i + 1}</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-1.5">{s.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 mt-12 text-center">
            <h4 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">お申込み・お問い合わせ</h4>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] mb-7 max-w-2xl mx-auto leading-[1.9]">コミュニケーション技術研修についてのご質問やお申込みは、お気軽にお問い合わせください。</p>
            <a href="/contact" className={BTN08_ON_NAVY}>コミュニケーション技術研修に申し込む</a>
          </div>
        </div>
      </section>

      <NavyBand no="VII" ja="よくある質問" en="FAQ" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="border-t-2 border-[#1a2942] max-w-4xl">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6">
                <h3 className="font-['Noto_Serif_JP',serif] font-medium text-[#1a2942] text-base sm:text-lg leading-[1.6] mb-2.5 flex gap-3"><span className="font-['Playfair_Display',serif] text-[#a38e65]">Q.</span><span>{f.q}</span></h3>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.95] flex gap-3"><span className="font-['Playfair_Display',serif] text-[#8f7a56]">A.</span><span>{f.a}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="VIII" ja="関連研修プログラム" en="Related Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-8">
            {RELATED.map((r) => (
              <a key={r.href} href={r.href} className="group bg-white border border-[#e2ddd2] p-8 flex flex-col hover:border-[#a38e65]/70 transition-colors duration-300">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 group-hover:text-[#8f7a56] transition-colors">{r.label}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9] mb-6">{r.desc}</p>
                <span className={`${TEXT_ARROW} mt-auto self-start`}>詳細を見る <span className="text-[#a38e65]">→</span></span>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/seminars" className={BTN08_ON_LIGHT}>セミナー・イベント一覧へ戻る</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
